import { create } from 'zustand'

export type PrefetchPhase =
  | 'idle'
  | 'fetching-lists'
  | 'fetching-details'
  | 'fetching-attachments'
  | 'done'
  | 'cancelled'

export type PrefetchState = {
  phase: PrefetchPhase
  totalIssues: number
  completedIssues: number
  totalIssueTypes: number
  completedIssueTypes: number
  currentIssueTypeName: string
  totalStages: number
  completedStages: number
  totalSvgs: number
  completedSvgs: number
  totalAttachments: number
  completedAttachments: number
  errorCount: number
  isVisible: boolean
  lastCompletedAt: number | null
  forceRefetchRequested: boolean
  /**
   * The stage currently being prefetched on-demand from the offline-cache
   * settings page. `null` means a global prefetch (or no prefetch in flight).
   * Used by per-row UI to show inline progress only on the active row.
   */
  targetStageId: string | null
  activeStageId: string | null
}

type PrefetchActions = {
  reset: () => void
  dismiss: () => void
  markCompleted: () => void
  requestForceRefetch: (targetStageId?: string) => void
  clearForceRefetchRequest: () => void
  setActiveStageId: (stageId: string | null) => void
}

const initialState: PrefetchState = {
  phase: 'idle',
  totalIssues: 0,
  completedIssues: 0,
  totalIssueTypes: 0,
  completedIssueTypes: 0,
  currentIssueTypeName: '',
  totalStages: 0,
  completedStages: 0,
  totalSvgs: 0,
  completedSvgs: 0,
  totalAttachments: 0,
  completedAttachments: 0,
  errorCount: 0,
  isVisible: false,
  lastCompletedAt: null,
  forceRefetchRequested: false,
  targetStageId: null,
  activeStageId: null,
}

export const usePrefetchStore = create<PrefetchState & PrefetchActions>(set => ({
  ...initialState,
  reset: () => set(initialState),
  dismiss: () => set({ isVisible: false }),
  markCompleted: () => set({ phase: 'done', lastCompletedAt: Date.now() }),
  requestForceRefetch: (targetStageId?: string) =>
    set({ forceRefetchRequested: true, targetStageId: targetStageId ?? null }),
  clearForceRefetchRequest: () => set({ forceRefetchRequested: false }),
  setActiveStageId: (stageId: string | null) => set({ activeStageId: stageId }),
}))
